/**
 * ResultSummary.jsx — Plain-English top-of-Overview summary.
 *
 * Answers "what happened?" before the user sees any chart:
 *   - Strongest response & its share
 *   - Response diversity in plain language
 *   - Optional "meaningful segment differences" callout when applicable
 */
import { useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getPrimaryDistribution } from '../../lib/metricLabels';

function describeDiversity(normEntropy) {
  if (normEntropy == null) return null;
  if (normEntropy >= 0.7) return 'high';
  if (normEntropy >= 0.35) return 'moderate';
  return 'low';
}

export default function ResultSummary({
  aggregation = null,
  simulation = null,
  preData = null,
  className,
  testId = 'result-summary',
}) {
  const lines = useMemo(() => {
    const out = [];
    const { percentages } = getPrimaryDistribution({ aggregation, preData });
    if (!percentages) return out;

    // Strongest response
    let topKey = null;
    let topPct = 0;
    for (const [k, v] of Object.entries(percentages)) {
      if (v > topPct) {
        topPct = v;
        topKey = k;
      }
    }
    if (topKey != null) {
      out.push(`The strongest response was "${topKey}" at ${topPct.toFixed(0)}%.`);
    }

    // Diversity
    const ne = simulation?.normalized_entropy ?? aggregation?.normalized_entropy;
    const diversity = describeDiversity(ne);
    if (diversity === 'high') {
      out.push('Response diversity is high — opinions are spread across multiple options.');
    } else if (diversity === 'moderate') {
      out.push('Response diversity is moderate — there is meaningful spread but a clear leading view.');
    } else if (diversity === 'low') {
      out.push('Response diversity is low — most respondents converged on one answer.');
    }

    // Segment differences (use aggregation.max_divergence if present, else preData.max_divergence)
    const maxDiv = aggregation?.max_divergence ?? preData?.max_divergence;
    if (maxDiv != null) {
      if (maxDiv >= 15) {
        out.push('Results show meaningful segment differences — review Demographic Breakdown.');
      } else if (maxDiv >= 8) {
        out.push('Some segment differences are present — worth a quick scan.');
      }
    }

    return out;
  }, [aggregation, simulation, preData]);

  if (lines.length === 0) return null;

  return (
    <Card className={cn('border border-slate-200 dark:border-border', className)} data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-warning-soft flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-warning" />
          </div>
          <div className="flex-1 space-y-1">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Result Summary</p>
            {lines.map((line, i) => (
              <p
                key={i}
                className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed"
                data-testid={`${testId}-line-${i}`}
              >
                {line}
              </p>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
